import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { User } from "@/types/user";
import { findUser, generateToken, getTokenTimeRemaining, verifyPassword, verifyToken, changeUserPassword, changeUsername } from "@/utils/auth";

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  changeUserUsername: (currentPassword: string, newUsername: string) => Promise<{success: boolean; message: string}>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sessionTimer, setSessionTimer] = useState<number | null>(null);

  // Token süresini kontrol eden fonksiyon
  const setupSessionTimeout = (token: string) => {
    // Daha önce çalışan timer varsa temizle
    if (sessionTimer) {
      clearTimeout(sessionTimer);
    }

    const timeRemaining = getTokenTimeRemaining(token);
    
    if (timeRemaining <= 0) {
      logout();
      return;
    }

    // Oturum süresinin %80'i geçtiğinde uyarı göster
    const warningTime = timeRemaining * 0.8;
    
    const timerId = window.setTimeout(() => {
      toast({
        title: "Oturum Süresi",
        description: "Oturum süreniz yakında dolacak. Lütfen işlemlerinizi tamamlayın.",
        duration: 10000,
      });
      
      // Oturum süresi dolduğunda otomatik çıkış
      const logoutTimerId = window.setTimeout(() => {
        toast({
          title: "Oturum Süresi Doldu",
          description: "Güvenlik nedeniyle oturumunuz sonlandırıldı.",
          duration: 5000,
        });
        logout();
      }, timeRemaining - warningTime);
      
      // İkinci timer'ı da saklıyoruz
      setSessionTimer(logoutTimerId);
    }, warningTime);
    
    setSessionTimer(timerId);
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("authToken");
      if (token) {
        const validatedUser = verifyToken(token);
        if (validatedUser) {
          setUser(validatedUser);
          setupSessionTimeout(token);
        } else {
          // Token geçersiz veya süresi dolmuş
          localStorage.removeItem("authToken");
          toast({
            title: "Oturum Süresi Doldu",
            description: "Güvenlik nedeniyle yeniden giriş yapmanız gerekiyor.",
            variant: "destructive",
          });
        }
      }
      setIsLoading(false);
    };

    checkAuth();

    // Temizlik fonksiyonu
    return () => {
      if (sessionTimer) {
        clearTimeout(sessionTimer);
      }
    };
  }, []);

  const login = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const foundUser = await findUser(username);
      
      if (!foundUser || !verifyPassword(password, foundUser.password)) {
        throw new Error("Geçersiz kullanıcı adı veya şifre");
      }
      
      const user: User = {
        id: foundUser.id,
        username: foundUser.username,
        role: foundUser.role
      };
      
      // JWT token oluştur
      const token = generateToken(user);
      
      // Token'ı localStorage'a kaydet
      localStorage.setItem("authToken", token);
      
      // Kullanıcı bilgisini state'e kaydet
      setUser(user);
      
      // Oturum zaman aşımı için timer kur
      setupSessionTimeout(token);
      
      toast({
        title: "Giriş Başarılı",
        description: "Yönetim paneline hoş geldiniz.",
      });
      
      navigate("/dashboard");
    } catch (error) {
      toast({
        title: "Giriş Başarısız",
        description: "Kullanıcı adı veya şifre hatalı.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    setUser(null);
    
    // Timer varsa temizle
    if (sessionTimer) {
      clearTimeout(sessionTimer);
      setSessionTimer(null);
    }
    
    toast({
      title: "Çıkış Yapıldı",
      description: "Başarıyla çıkış yapıldı.",
    });
    
    navigate("/login");
  };

  // Şifre değiştirme fonksiyonu
  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const userData = await findUser(user.username);
      if (!userData) return false;
      
      // Mevcut şifre doğru mu kontrol et
      if (!verifyPassword(currentPassword, userData.password)) {
        toast({
          title: "Şifre Değiştirme Başarısız",
          description: "Mevcut şifre yanlış.",
          variant: "destructive",
        });
        return false;
      }
      
      // Şifreyi veritabanında güncelle
      const success = await changeUserPassword(userData.id, newPassword);
      
      if (!success) {
        throw new Error("Şifre güncellenirken bir hata oluştu");
      }
      
      toast({
        title: "Şifre Değiştirildi",
        description: "Şifreniz başarıyla güncellendi.",
      });
      
      // Oturumu yenile
      logout();
      return true;
    } catch (error) {
      console.error("Şifre değiştirme hatası:", error);
      toast({
        title: "Şifre Değiştirme Hatası",
        description: "Beklenmeyen bir hata oluştu.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Kullanıcı adı değiştirme fonksiyonu
  const changeUserUsername = async (currentPassword: string, newUsername: string): Promise<{success: boolean; message: string}> => {
    if (!user) return { success: false, message: "Kullanıcı bulunamadı." };
    
    try {
      const userData = await findUser(user.username);
      if (!userData) return { success: false, message: "Kullanıcı bulunamadı." };
      
      // Mevcut şifre doğru mu kontrol et
      if (!verifyPassword(currentPassword, userData.password)) {
        toast({
          title: "Kullanıcı Adı Değiştirme Başarısız",
          description: "Şifre yanlış.",
          variant: "destructive",
        });
        return { success: false, message: "Şifre yanlış." };
      }
      
      // Kullanıcı adını güncelle
      const result = await changeUsername(userData.id, newUsername);
      
      if (!result.success) {
        toast({
          title: "Kullanıcı Adı Değiştirme Başarısız",
          description: result.message,
          variant: "destructive",
        });
        return result;
      }
      
      toast({
        title: "Kullanıcı Adı Değiştirildi",
        description: "Kullanıcı adınız başarıyla güncellendi.",
      });
      
      // Oturumu yenile
      logout();
      return { success: true, message: "Kullanıcı adınız başarıyla güncellendi." };
    } catch (error) {
      console.error("Kullanıcı adı değiştirme hatası:", error);
      toast({
        title: "Kullanıcı Adı Değiştirme Hatası",
        description: "Beklenmeyen bir hata oluştu.",
        variant: "destructive",
      });
      return { success: false, message: "Beklenmeyen bir hata oluştu." };
    }
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
    changePassword,
    changeUserUsername
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
